import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Code,
  Plus,
  Trash2,
  Upload,
  Play,
  Save,
  AlertCircle,
  CheckCircle,
  Clock,
  Database,
  Tag,
  BookOpen
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import { handleApiError } from '@/lib/utils'

interface CreateCodingChallengeModalProps {
  open: boolean
  onClose: () => void
  onProblemCreated: (newProblem?: any) => void
}

interface TestCase {
  input: string
  expected_output: string
  is_example: boolean
  explanation?: string
}

interface Example {
  input: string
  output: string
  explanation: string
}

interface StarterCode {
  [language: string]: string
}

const SUPPORTED_LANGUAGES = [
  'javascript',
  'python',
  'java',
  'cpp',
  'c',
  'csharp',
  'go',
  'rust',
  'typescript',
  'php'
]

const DIFFICULTY_OPTIONS = ['easy', 'medium', 'hard']

const CATEGORY_OPTIONS = [
  'Array',
  'String',
  'Hash Table',
  'Dynamic Programming',
  'Math',
  'Two Pointers',
  'Binary Search',
  'Sorting',
  'Greedy',
  'Tree',
  'Graph',
  'DFS',
  'BFS',
  'Backtracking',
  'Stack',
  'Queue',
  'Heap',
  'Linked List',
  'Bit Manipulation',
  'Sliding Window'
]

export default function CreateCodingChallengeModal({
  open,
  onClose,
  onProblemCreated
}: CreateCodingChallengeModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    difficulty: 'medium' as const,
    category: '',
    tags: [] as string[],
    timeLimit: 3600,
    memoryLimit: 512,
    supportedLanguages: ['javascript', 'python', 'java'] as string[]
  })

  const [starterCode, setStarterCode] = useState<StarterCode>({
    javascript: '',
    python: '',
    java: ''
  })

  const [testCases, setTestCases] = useState<TestCase[]>([
    { input: '', expected_output: '', is_example: true, explanation: '' }
  ])

  const [examples, setExamples] = useState<Example[]>([
    { input: '', output: '', explanation: '' }
  ])

  const [hints, setHints] = useState<string[]>([''])
  const [editorialSolution, setEditorialSolution] = useState('')
  const [customCategory, setCustomCategory] = useState('')
  const [newTag, setNewTag] = useState('')

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      // Reset form when modal opens
      resetForm()
    }
  }, [open])

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      difficulty: 'medium',
      category: '',
      tags: [],
      timeLimit: 3600,
      memoryLimit: 512,
      supportedLanguages: ['javascript', 'python', 'java']
    })
    setStarterCode({
      javascript: '',
      python: '',
      java: ''
    })
    setTestCases([{ input: '', expected_output: '', is_example: true, explanation: '' }])
    setExamples([{ input: '', output: '', explanation: '' }])
    setHints([''])
    setEditorialSolution('')
    setCustomCategory('')
    setNewTag('')
    setErrors({})
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) newErrors.title = 'Title is required'
    if (formData.title.length < 3) newErrors.title = 'Title must be at least 3 characters'
    if (!formData.description.trim()) newErrors.description = 'Description is required'
    if (formData.description.length < 10) newErrors.description = 'Description must be at least 10 characters'
    if (!formData.category && !customCategory) newErrors.category = 'Category is required'
    if (formData.supportedLanguages.length === 0) newErrors.supportedLanguages = 'At least one language is required'

    // Validate test cases
    const validTestCases = testCases.filter(tc => tc.input.trim() && tc.expected_output.trim())
    if (validTestCases.length === 0) newErrors.testCases = 'At least one complete test case is required'

    // Validate examples
    const validExamples = examples.filter(ex => ex.input.trim() && ex.output.trim())
    if (validExamples.length === 0) newErrors.examples = 'At least one complete example is required'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setLoading(true)
    try {
      const category = customCategory.trim() || formData.category
      const validTestCases = testCases.filter(tc => tc.input.trim() && tc.expected_output.trim())
      const validExamples = examples.filter(ex => ex.input.trim() && ex.output.trim())
      const validHints = hints.filter(hint => hint.trim())
      
      // Prepare starter code object with only selected languages
      const starterCodeForLanguages: StarterCode = {}
      formData.supportedLanguages.forEach(lang => {
        if (starterCode[lang]?.trim()) {
          starterCodeForLanguages[lang] = starterCode[lang].trim()
        }
      })

      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        difficulty: formData.difficulty,
        category,
        tags: formData.tags,
        timeLimit: formData.timeLimit,
        memoryLimit: formData.memoryLimit,
        supportedLanguages: formData.supportedLanguages,
        starterCode: starterCodeForLanguages,
        testCases: validTestCases,
        examples: validExamples,
        hints: validHints,
        editorialSolution: editorialSolution.trim()
      }

      const response = await apiClient.createCodingProblem(payload)
      
      if (response.success) {
        onProblemCreated(response.data)
        onClose()
      } else {
        setErrors({ submit: (response.error as any)?.message || 'Failed to create problem' })
      }
    } catch (error) {
      setErrors({ submit: handleApiError(error) })
    } finally {
      setLoading(false)
    }
  }

  const addTestCase = () => {
    setTestCases([...testCases, { input: '', expected_output: '', is_example: false, explanation: '' }])
  }

  const removeTestCase = (index: number) => {
    setTestCases(testCases.filter((_, i) => i !== index))
  }

  const updateTestCase = (index: number, field: keyof TestCase, value: string | boolean) => {
    const updated = [...testCases]
    updated[index] = { ...updated[index], [field]: value }
    setTestCases(updated)
  }

  const addExample = () => {
    setExamples([...examples, { input: '', output: '', explanation: '' }])
  }

  const removeExample = (index: number) => {
    setExamples(examples.filter((_, i) => i !== index))
  }

  const updateExample = (index: number, field: keyof Example, value: string) => {
    const updated = [...examples]
    updated[index] = { ...updated[index], [field]: value }
    setExamples(updated)
  }

  const addHint = () => {
    setHints([...hints, ''])
  }

  const removeHint = (index: number) => {
    setHints(hints.filter((_, i) => i !== index))
  }

  const updateHint = (index: number, value: string) => {
    const updated = [...hints]
    updated[index] = value
    setHints(updated)
  }

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }))
      setNewTag('')
    }
  }

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }))
  }

  const toggleLanguage = (language: string) => {
    setFormData(prev => {
      const isSelected = prev.supportedLanguages.includes(language)
      if (isSelected) {
        return {
          ...prev,
          supportedLanguages: prev.supportedLanguages.filter(l => l !== language)
        }
      } else {
        // Add starter code field for new language
        if (!starterCode[language]) {
          setStarterCode(prev => ({ ...prev, [language]: '' }))
        }
        return {
          ...prev,
          supportedLanguages: [...prev.supportedLanguages, language]
        }
      }
    })
  }

  if (!open) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <Code className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Create Coding Challenge</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Problem Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Two Sum, Valid Parentheses"
                />
                {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Difficulty *
                </label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {DIFFICULTY_OPTIONS.map(diff => (
                    <option key={diff} value={diff}>
                      {diff.charAt(0).toUpperCase() + diff.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <div className="space-y-2">
                  <select
                    value={formData.category}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, category: e.target.value }))
                      setCustomCategory('')
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select category...</option>
                    {CATEGORY_OPTIONS.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="_custom">Custom Category</option>
                  </select>
                  {formData.category === '_custom' && (
                    <input
                      type="text"
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      placeholder="Enter custom category"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>
                {errors.category && <p className="text-red-600 text-sm mt-1">{errors.category}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Time Limit (seconds)
                </label>
                <input
                  type="number"
                  min="30"
                  max="3600"
                  value={formData.timeLimit}
                  onChange={(e) => setFormData(prev => ({ ...prev, timeLimit: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Database className="w-4 h-4 inline mr-1" />
                  Memory Limit (MB)
                </label>
                <input
                  type="number"
                  min="16"
                  max="1024"
                  value={formData.memoryLimit}
                  onChange={(e) => setFormData(prev => ({ ...prev, memoryLimit: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Problem Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe the problem in detail. Include constraints, input/output format, etc."
              />
              {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Tag className="w-4 h-4 inline mr-1" />
                Tags
              </label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Add tags..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Supported Languages */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supported Languages *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {SUPPORTED_LANGUAGES.map((language) => (
                  <label key={language} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.supportedLanguages.includes(language)}
                      onChange={() => toggleLanguage(language)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 capitalize">{language}</span>
                  </label>
                ))}
              </div>
              {errors.supportedLanguages && <p className="text-red-600 text-sm mt-1">{errors.supportedLanguages}</p>}
            </div>

            {/* Starter Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Starter Code (Optional)
              </label>
              <div className="space-y-4">
                {formData.supportedLanguages.map((language) => (
                  <div key={language}>
                    <label className="block text-xs font-medium text-gray-600 mb-1 capitalize">
                      {language}
                    </label>
                    <textarea
                      value={starterCode[language] || ''}
                      onChange={(e) => setStarterCode(prev => ({
                        ...prev,
                        [language]: e.target.value
                      }))}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      placeholder={`Enter ${language} starter code...`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Examples */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Examples *
              </label>
              <div className="space-y-4">
                {examples.map((example, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-medium text-gray-700">Example {index + 1}</h4>
                      {examples.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeExample(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Input</label>
                        <textarea
                          value={example.input}
                          onChange={(e) => updateExample(index, 'input', e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                          placeholder="Example input..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Output</label>
                        <textarea
                          value={example.output}
                          onChange={(e) => updateExample(index, 'output', e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                          placeholder="Expected output..."
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Explanation</label>
                      <textarea
                        value={example.explanation}
                        onChange={(e) => updateExample(index, 'explanation', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Explain this example..."
                      />
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addExample}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Example</span>
                </button>
              </div>
              {errors.examples && <p className="text-red-600 text-sm mt-1">{errors.examples}</p>}
            </div>

            {/* Test Cases */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Test Cases *
              </label>
              <div className="space-y-4">
                {testCases.map((testCase, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-sm font-medium text-gray-700">Test Case {index + 1}</h4>
                        <label className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            checked={testCase.is_example}
                            onChange={(e) => updateTestCase(index, 'is_example', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-600">Public example</span>
                        </label>
                      </div>
                      {testCases.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTestCase(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Input</label>
                        <textarea
                          value={testCase.input}
                          onChange={(e) => updateTestCase(index, 'input', e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                          placeholder="Test input..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Expected Output</label>
                        <textarea
                          value={testCase.expected_output}
                          onChange={(e) => updateTestCase(index, 'expected_output', e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                          placeholder="Expected output..."
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Explanation (Optional)</label>
                      <input
                        type="text"
                        value={testCase.explanation || ''}
                        onChange={(e) => updateTestCase(index, 'explanation', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Explain this test case..."
                      />
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addTestCase}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Test Case</span>
                </button>
              </div>
              {errors.testCases && <p className="text-red-600 text-sm mt-1">{errors.testCases}</p>}
            </div>

            {/* Hints */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <BookOpen className="w-4 h-4 inline mr-1" />
                Hints (Optional)
              </label>
              <div className="space-y-2">
                {hints.map((hint, index) => (
                  <div key={index} className="flex space-x-2">
                    <input
                      type="text"
                      value={hint}
                      onChange={(e) => updateHint(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={`Hint ${index + 1}...`}
                    />
                    {hints.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeHint(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addHint}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Hint</span>
                </button>
              </div>
            </div>

            {/* Editorial Solution */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Editorial Solution (Optional)
              </label>
              <textarea
                value={editorialSolution}
                onChange={(e) => setEditorialSolution(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="Provide a detailed solution explanation with code examples..."
              />
            </div>

            {/* Form Errors */}
            {errors.submit && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-red-600 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Create Problem</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}